import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import CommunityWall from '../../components/common/CommunityWall';
import { apiRequest, shortDate, canSharePhotos, downloadPhoto, sharePhoto } from '../../utils';
import './MemberHome.css';
import './CompanyFeed.css';
import './PhotoWall.css';

/* The photo wall — F8.

   Every picture from every event this person may see, in one place: three collections (the event
   albums, the pictures attached to posts, the event posters) and three sequences (the newest, the
   most liked, a shuffle). The albums stay where they are — this is the way to look at them at once.

   ★ A tile is a POINTER, exactly as a feed card is. Liking a photo here writes the same
   ``data.Reaction`` row that liking it in the album writes, and a comment lands in the same
   thread the event page shows. One conversation, three screens.

   ★ The wall never re-filters what it received. Which pictures are on it was decided in the
   query, by the same rule the feed and the event page use; a tile the screen received and
   declined to draw would already have been published.

   ★ A write patches ONE tile. The stream answers a write with a fresh first page and throws away
   what was fetched after it — right for a list you read downwards, wrong for a wall you are three
   screens into with a picture open. So the server sends the row that changed and nothing here is
   reloaded. */

const MAX_BODY = 4000;
const PAGE = 24;
const ORDERS = ['recent', 'liked', 'random'];
/* WHICH pictures, as opposed to in which order. Four different things the app holds images of,
   and somebody looking for one of them is not browsing the other three — so these are chips of
   their own rather than more orderings. A poster has no feed row behind it: nothing to like,
   nothing to say under it, and its tile opens the event instead of a lightbox.

   ★ The missions chip is the one that is not always there. The other three exist for every
   business unit; a mission is a programme somebody had to start, and a chip opening on "nothing
   yet" would teach the reader the wall is empty. The server says whether there is anything
   (``has_missions``) and an older payload simply keeps the wall as it was.

   ★ The fifth chip, `people`, is the odd one out and deliberately so: it is not pictures OF
   things but the faces of the colleagues who took them, and it is served by its own endpoint
   (`/api/company/community`). It lives here because this is the screen somebody is already on
   when they want to look at people rather than read about them — and because a wall of faces
   with its own page in the menu is a directory nobody would open twice. Offered only where
   there is a roster behind it (`has_people`), on the same rule as the missions chip. */
/* ★ The sixth chip, `games`, is the other read-only one: two thousand pictures of what was
   actually PLAYED have been in the system for years (`data.Reglementen.FotoUrl`) and no screen in
   the app ever showed one. Gated on `has_games` like the missions chip, because a business unit
   may hold none of them.

   ★★ The server only sends games the teams have been TOLD about. That is not a filter of its
   own — it is the platform's one answer to "do the players see this game", the same announcement
   row the joker picker inner-joins — and it matters here because an organiser keeping the day's
   games a surprise must not find them on everybody's wall the week before. Which is also why the
   games screen's own hint says a picture reaches this album once the game is visible. */
/* ★ 'events' — the albums of OUR events the company took part in (Hercules Trophy, Pink Ladies
   Games, Fabrica, any brand under the same parent). The company admin's view only, offered when
   one of those albums holds pictures (`has_albums`). Read-only tiles, like a game's. */
const SOURCES = ['photos', 'events', 'feed', 'posters', 'missions', 'games', 'people'];
/* A poster and a game are both sorted by the date of the event; "most liked" would be a wall of
   zeroes, because neither of them has a feed row for a heart to hang off. */
const POSTER_ORDERS = ['recent', 'random'];
const FLAT_SOURCES = ['posters', 'games', 'events'];
/* The same six the feed and the event page offer — the queue in Machina triages on the reason. */
const REPORT_REASONS = ['abuse', 'harassment', 'sexual', 'spam', 'privacy', 'other'];

const Avatar = ({ url, name }) => (
  url
    ? <img className="cf-avatar" src={url} alt="" />
    : <span className="cf-avatar cf-avatar--empty">{(name || '?').trim().charAt(0) || '?'}</span>
);

const PhotoWall = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [busy, setBusy] = useState(false);
  // Kept apart from `busy`: fetching a picture to save is not a reason to freeze liking, tagging
  // or the next page of the wall.
  const [saving, setSaving] = useState(false);

  // A property of the browser, not of the photograph, so it is asked once.
  const [shareable] = useState(canSharePhotos);
  const [notice, setNotice] = useState(null);
  const [order, setOrder] = useState('recent');
  /* ``?source=`` deep-links, which is how the member home's Community card lands straight on the
     faces. Same idiom as the engagement screen's ``?tab=``: an unknown value is the album
     photos, never an error. */
  const [source, setSource] = useState(() => {
    const wanted = new URLSearchParams(window.location.search).get('source');
    return SOURCES.includes(wanted) ? wanted : 'photos';
  });
  const [scope, setScope] = useState(null);        // null | 'labs' | 'company'
  const [eventId, setEventId] = useState(null);
  const [shuffle, setShuffle] = useState(0);       // bumping it re-rolls a random page
  const [open, setOpen] = useState(null);          // id of the tile in the lightbox
  const [draft, setDraft] = useState('');
  const [reporting, setReporting] = useState(false);
  const [reportReason, setReportReason] = useState('abuse');
  const [reportNote, setReportNote] = useState('');
  /* Naming somebody in the open picture. ``people`` is who may be named on THIS card — fetched on
     the first press rather than with the wall, because it is one event's guest list and the wall
     is a mix of evenings: sending all of them with every page would be a payload of names nobody
     asked to see. `null` while it is on its way. */
  const [tagging, setTagging] = useState(false);
  const [people, setPeople] = useState(null);
  // Whether the photo payload has ever arrived. A ref rather than state: it only decides whether
  // a fetch is skipped, and putting it in state would rebuild ``load`` and cause the fetch it is
  // there to prevent.
  const loaded = useRef(false);
  const [turned, setTurned] = useState({});   // tile id -> quarter-turns asked for here

  /* Every request carries the whole view — the ordering and both filters — because the write
     routes answer with the page the caller is looking at, and a like that came back as somebody
     else's page would redraw the wall out from under them. */
  const query = useCallback((extra) => {
    const q = [`limit=${PAGE}`, `order=${order}`, `source=${source}`];
    if (scope) q.push(`scope=${scope}`);
    if (eventId) q.push(`event=${encodeURIComponent(eventId)}`);
    if (extra) q.push(extra);
    return q.join('&');
  }, [order, source, scope, eventId]);

  /* What a write is sent with: the same view, plus the flag that says which screen is asking. */
  const writeQuery = useCallback(() => `?view=photos&${query()}`, [query]);

  const load = useCallback(() => {
    // The people chip is served by its own endpoint, inside its own component. Asking the photo
    // route for a source it does not know would answer with the album photos — a whole page
    // fetched to be thrown away, and the payload already in state is the one the chips read.
    //
    // ★ The FIRST load still happens, whatever the chip: this payload is where the chips
    // themselves come from, and the flag that says whether the people chip may be drawn at all.
    // Landing straight on ?source=people and skipping it would leave the screen on its spinner.
    if (source === 'people' && loaded.current) return;
    apiRequest('GET', `/company/photos?${query()}`)
      .then((res) => {
        /* ★ A Hercules Trophy or Pink Ladies Games company has nothing on the stream — its
           pictures are in the albums of the events it took part in. Landing on an empty wall with
           the photographs one chip away reads as "no photos", so the first load goes there. */
        const firstLoad = !loaded.current;
        loaded.current = true;
        if (firstLoad && source === 'photos' && res.has_albums && !(res.items || []).length
            && !new URLSearchParams(window.location.search).get('source')) {
          setData(res);
          setSource('events');
          return;
        }
        setData(res);
        setItems(res.items || []);
        setCursor(res.next_cursor);
        setHasMore(!!res.has_more);
        // A bookmarked ?source=people on a wall that has no company behind it: the chip is not
        // drawn, so the view falls back to the pictures rather than to an empty screen with no
        // way off it.
        if (res.has_people === false) setSource((s) => (s === 'people' ? 'photos' : s));
      })
      .catch(() => navigate('/member-home'));
    // ``shuffle`` is in the dependencies on purpose: pressing "surprise me" again asks the server
    // for a new roll, and there is nothing else about the request that changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, query, shuffle]);

  useEffect(() => { load(); }, [load]);

  const loadMore = () => {
    if (busy || !cursor) return;
    setBusy(true);
    apiRequest('GET', `/company/photos?${query(`cursor=${encodeURIComponent(cursor)}`)}`)
      .then((res) => {
        setItems((all) => [...all, ...(res.items || [])]);
        setCursor(res.next_cursor);
        setHasMore(!!res.has_more);
      })
      .catch(() => setNotice(t('saveFailed')))
      .finally(() => setBusy(false));
  };

  /* One tile, replaced in place. Keeps the scroll, keeps the lightbox, keeps the pages already
     fetched — the wall is not a list that starts again every time somebody presses a heart. */
  const patch = (card) => {
    if (!card) return;
    setItems((all) => all.map((it) => (it.id === card.id ? card : it)));
  };

  const act = (promise) => {
    if (busy) return Promise.resolve();
    setBusy(true);
    setNotice(null);
    return promise
      .then((res) => { patch(res.card); return res; })
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  if (data === null) {
    return (
      <Base activeNav="home">
        <Card><span className="auth__spinner" /></Card>
      </Base>
    );
  }

  const me = data.me;
  /* A tile draws whichever of the four shapes it came as. The card carries the answer — an album
     photo has `photo`, a post has `post`, a poster has `poster`, a mission answer has `mission` —
     so nothing here has to remember which chip is on.

     ★ A mission answer's picture sits in `answer`, the same field a social answer keeps its link
     in; the wall's missions source only ever sends the picture kind, so it is an image here. */
  const isPoster = (card) => card.subject_type === 'poster';
  /* A game tile is read-only in exactly the way a poster is — no feed row behind it, so no
     hearts, no thread, nobody to name in it. What it is NOT is a signpost: pressing it opens the
     picture, because a photograph of the blindfolded maze is the thing somebody came to look at,
     not a way to reach the evening it was played on. */
  const isGame = (card) => card.subject_type === 'game';
  /* A picture out of the album of one of our events: no feed row either, so read-only like a
     game — and it opens the picture, with the event it was taken at under it. */
  const isAlbum = (card) => card.subject_type === 'album';
  const isFlat = (card) => isPoster(card) || isGame(card) || isAlbum(card);
  const bodyOf = (card) => (isPoster(card) ? card.poster : isGame(card) ? card.game
                            : isAlbum(card) ? card.album
                              : card.mission || card.post || card.photo) || {};
  const imageOf = (card) => (isFlat(card) ? bodyOf(card).url
                             : (card.mission || {}).answer || bodyOf(card).image_url
                               || (card.photo || {}).url);
  /* What the browser is given. Turning a photograph replaces the bytes UNDER THE SAME NAME —
     that name is the picture's whole identity, every like, tag and comment hangs off the string —
     so the address of a turned picture is the address of the crooked one, and the copy already on
     the screen would never change. The turns asked for here are stamped on it to make the browser
     fetch it again. A reload needs no help: the turned blob is written ``no-cache``. */
  const srcOf = (card) => {
    const url = imageOf(card);
    return url && turned[card.id] ? `${url}?v=${turned[card.id]}` : url;
  };
  /* Save the picture, or hand it to the phone's share sheet. Fed ``srcOf`` rather than the bare
     address for the reason ``srcOf`` exists at all: after a turn those are two different
     photographs, and the one somebody wants to send on is the one they are looking at.

     Offered on every kind of picture the wall carries, not just album photos — a post's
     photograph and a mission answer are things this person is already allowed to look at, and
     long-pressing them already worked. This only makes it a button. */
  const savePicture = async (card, action) => {
    const url = srcOf(card);
    if (saving || !url) return;
    setSaving(true);
    try {
      await action(url);
    } finally {
      setSaving(false);
    }
  };
  /* What the tile says it is. A mission answer names the MISSION rather than the evening it was
     handed in on: "Take a photo of your desk" is what makes the picture legible, and plenty of
     missions hang off no event at all. */
  const whereOf = (card) => ((card.mission || {}).mission_name
                             /* ★ And a game names the GAME, not the evening — "Truck Pulling" is
                                what makes the picture legible; the event is under it in the
                                lightbox for whoever wants to know which night that was. */
                             || (card.game || {}).name
                             || (card.event ? card.event.name : ''));
  const canModerate = !!data.can_moderate;
  // Absent means yes: an older payload must not silence everybody's replies.
  const canPost = data.can_post !== false;
  const wallEvents = data.events || [];
  const hasCompany = !!data.has_company;
  /* The one chip that is not a collection of pictures. Everything below the chip row is about
     photographs, so it is all asked to stand aside for it. */
  const isPeople = source === 'people';
  const photo = open ? items.find((it) => it.id === open) : null;

  const like = (card) => act(apiRequest('POST', `/company/feed/${card.id}/like${writeQuery()}`));

  const comment = (card) => {
    const body = draft.trim();
    if (!body) return;
    setDraft('');
    act(apiRequest('POST', `/company/feed/${card.id}/comment${writeQuery()}`,
                   { body: body.slice(0, MAX_BODY) }));
  };

  /* Who may be named in the open picture. Asked per CARD: the server answers with that event's
     people minus anybody who switched tagging off in their profile, so the list is already the
     right one and this screen never filters it. */
  const loadPeople = (card) => {
    setTagging(true);
    if (people !== null) return;
    apiRequest('GET', `/company/feed/${card.id}/people`)
      .then((res) => setPeople(res.people || []))
      .catch(() => { setPeople([]); setNotice(t('saveFailed')); });
  };

  const tag = (card, partnerId) => {
    setTagging(false);
    act(apiRequest('POST', `/company/feed/${card.id}/tag${writeQuery()}`,
                   { partner_id: partnerId }));
  };

  const untag = (card, partnerId) =>
    act(apiRequest('DELETE', `/company/feed/${card.id}/tag${writeQuery()}`,
                   { partner_id: partnerId }));

  /* Taking a picture off the wall takes the CARD off, never the photograph: the album on the
     event page owns the file, and its takedown is the flow that deletes for real. So the tile
     goes here and the wall does not pretend to have done more than it did. */
  const remove = (card) => {
    if (busy) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('feedRemoveConfirm'))) return;
    setBusy(true);
    setNotice(null);
    apiRequest('DELETE', `/company/feed/${card.id}${writeQuery()}`)
      .then(() => {
        setItems((all) => all.filter((it) => it.id !== card.id));
        setOpen(null);
      })
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  /* Turning a picture that landed sideways. It writes to the EVENT's album route, not to a wall
     route of its own: the album holds the bytes and the wall only points at them, so there is one
     place a JPEG is re-encoded and one rule about who may — whoever took it, and whoever runs the
     event. The server asks that question again whatever this screen drew.

     ★ The answer is the album, which is not a wall card, so nothing is patched: only the address
     changes (see ``srcOf``). Everything hanging off the picture — its likes, its tags, what people
     said under it — is untouched by a turn and was already right on the screen. */
  const rotate = (card, turns) => {
    if (busy) return;
    const name = (card.photo || {}).name;
    const eventOfCard = card.event ? card.event.id : null;
    if (!name || !eventOfCard) return;
    setBusy(true);
    setNotice(null);
    apiRequest('POST', `/event/${eventOfCard}/photos/rotate`, { name, turns })
      .then(() => setTurned((all) => ({ ...all, [card.id]: (all[card.id] || 0) + 1 })))
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  /* Filing an objection changes nothing on the screen, and that is the thing the person has to
     be told — so the answer is a sentence, not a redraw. */
  const sendReport = (card) => {
    if (busy) return;
    setReporting(false);
    setBusy(true);
    setNotice(null);
    apiRequest('POST', `/company/feed/${card.id}/report${writeQuery()}`,
               { reason: reportReason, note: reportNote.trim() })
      .then(() => { setReportNote(''); setNotice(t('reportThanks')); })
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  const pick = (next) => {
    setOpen(null);
    if (next === order && next === 'random') { setShuffle((n) => n + 1); return; }
    setOrder(next);
  };

  /* Switching source is not switching filter: it is a different set of pictures, so the pages
     already fetched and the event chosen for the old one both go. Keeping the cursor would page
     one collection with the other's bookmark. */
  const pickSource = (next) => {
    if (next === source) return;
    setOpen(null);
    setItems([]);
    setCursor(null);
    setHasMore(false);
    setEventId(null);
    if (FLAT_SOURCES.includes(next) && !POSTER_ORDERS.includes(order)) setOrder('recent');
    setSource(next);
  };

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="camera" theme="primary" className="me-2" />
          {t('photoWallTitle')}
        </Title>
        <p className="bravos-q__hint mb-2">{t('photoWallNote')}</p>

        {/* WHICH pictures. One collection at a time; missions only where there are any, and
            people only where there is a company whose people they would be. */}
        <div className="cf-filters">
          {SOURCES.filter((s) => (s !== 'missions' || data.has_missions)
                                 && (s !== 'events' || data.has_albums)
                                 && (s !== 'games' || data.has_games)
                                 && (s !== 'people' || data.has_people)).map((s) => (
            <button
              key={s}
              type="button"
              className={`cf-chip ${source === s ? 'is-on' : ''}`}
              onClick={() => pickSource(s)}
            >
              {t(`photoSource_${s}`)}
            </button>
          ))}
        </div>

        {/* The sequences. Not filters — every one of them shows the same set.

            ★ None of the three rows below applies to the people chip: faces are not ordered by
            likes, an office is not "only ours", and nobody was taken at an event. They are
            hidden rather than disabled — a row of dead chips reads as broken. */}
        {!isPeople && (
        <div className="cf-filters">
          {(FLAT_SOURCES.includes(source) ? POSTER_ORDERS : ORDERS).map((o) => (
            <button
              key={o}
              type="button"
              className={`cf-chip ${order === o ? 'is-on' : ''}`}
              onClick={() => pick(o)}
            >
              {t(`photoOrder_${o}`)}
            </button>
          ))}
          {order === 'random' && (
            <button type="button" className="cf-chip pw-chip--action"
                    disabled={busy}
                    onClick={() => setShuffle((n) => n + 1)}>
              <Icon name="shuffle" className="me-1" />{t('photoShuffle')}
            </button>
          )}
        </div>
        )}

        {/* The same two filters the stream offers, for the same reasons: "only ours" belongs to
            somebody who HAS a company, and the dropdown names only events with pictures on it. */}
        {!isPeople && ((hasCompany && source !== 'events') || wallEvents.length > 1) && (
          <div className="cf-filters">
            {hasCompany && source !== 'events' && (
              <>
                <button type="button"
                        className={`cf-chip ${!scope ? 'is-on' : ''}`}
                        onClick={() => { setOpen(null); setScope(null); }}>{t('feedAll')}</button>
                <button type="button"
                        className={`cf-chip ${scope === 'company' ? 'is-on' : ''}`}
                        onClick={() => { setOpen(null); setScope('company'); }}>
                  {t('feedOnlyOurs')}
                </button>
                <button type="button"
                        className={`cf-chip ${scope === 'labs' ? 'is-on' : ''}`}
                        onClick={() => { setOpen(null); setScope('labs'); }}>
                  {t('feedOnlyLabs')}
                </button>
              </>
            )}
            {wallEvents.length > 1 && (
              <select className="cf-select"
                      value={eventId || ''}
                      onChange={(e) => { setOpen(null); setEventId(e.target.value || null); }}>
                <option value="">{t('feedAllEvents')}</option>
                {wallEvents.map((e) => (
                  <option key={e.id} value={e.id}>{e.name}</option>
                ))}
              </select>
            )}
          </div>
        )}
        {notice && <p className="bravos-cal__notice">{notice}</p>}
      </Card>

      {/* The people this person works with, in their own component — the same wall the admin
          gets on the Community tab of /engagement, off the same endpoint. Nothing about it is a
          copy: a face here and a face there are one row, one screen, one implementation. */}
      {isPeople && (
        <Card>
          {/* Faces only. This is the photo wall — somebody arriving on the People chip came to
              look at the people here, and a grey initial is not one of them. The admin's own
              Community tab still lists everybody, because finding who has no photograph is that
              screen's job. */}
          <CommunityWall facesOnly />
        </Card>
      )}

      {!isPeople && items.length === 0 && (
        <Card>
          <p className="ce-note">
            {t(source === 'feed' ? 'photoWallEmptyFeed'
              : source === 'posters' ? 'photoWallEmptyPosters'
                : source === 'missions' ? 'photoWallEmptyMissions'
                  : source === 'games' ? 'photoWallEmptyGames'
                    : source === 'events' ? 'photoWallEmptyEvents' : 'photoWallEmpty')}
          </p>
        </Card>
      )}

      {!isPeople && items.length > 0 && (
        <div className="pw-gallery">
          {items.map((card) => (
            <div className="pw-tile" key={card.id}>
              <button type="button"
                      className={`pw-tile__btn ${isPoster(card) ? 'pw-tile__btn--go' : ''}`}
                      onClick={() => {
                /* A poster is a signpost, so it goes where it points. Everything else opens
                   where its conversation is — or, for a game, just opens the picture. */
                if (isPoster(card)) { navigate(`/public-event/${card.event.id}`); return; }
                /* The people list belongs to the picture that was open — a different tile is
                   very often a different evening, so it is dropped rather than reused. */
                setOpen(card.id); setReporting(false); setDraft('');
                setTagging(false); setPeople(null);
              }}>
                <img className="pw-tile__img" src={srcOf(card)} alt="" loading="lazy" />
                <span className="pw-tile__foot">
                  {!isFlat(card) && (
                    <>
                      <span className={`pw-count ${bodyOf(card).liked ? 'is-on' : ''}`}>
                        <Icon name="heart" /> {bodyOf(card).likes || 0}
                      </span>
                      {(bodyOf(card).comments || []).length > 0 && (
                        <span className="pw-count">
                          <Icon name="comment" /> {bodyOf(card).comments.length}
                        </span>
                      )}
                    </>
                  )}
                  {isFlat(card) && (
                    <span className="pw-count">
                      <Icon name="calendar" /> {shortDate(card.created_at, i18n.language)}
                    </span>
                  )}
                  {whereOf(card) && <span className="pw-tile__where">{whereOf(card)}</span>}
                </span>
              </button>
            </div>
          ))}
        </div>
      )}

      {hasMore && (
        <Card>
          <button
            type="button"
            className="bravos-submit bravos-submit--ghost"
            disabled={busy}
            onClick={loadMore}
          >
            {t('showMore')}
          </button>
        </Card>
      )}

      {/* One picture, full size, with everything that hangs off it — which for a game is
          nothing but its name and its rules. */}
      {photo && (photo.photo || photo.post || photo.mission || photo.game || photo.album) && (
        <div className="pw-lightbox" role="dialog" aria-modal="true">
          <button type="button" className="pw-lightbox__close" aria-label={t('close')}
                  onClick={() => { setOpen(null); setReporting(false); setDraft('');
                                   setTagging(false); setPeople(null); }}>
            <Icon name="xmark" />
          </button>
          <div className="pw-lightbox__inner">
            <img src={srcOf(photo)} alt="" />

            <div className="pw-lb__head">
              {/* ★ Nobody took this one. A game's picture is the organiser's, filed on the day
                  itself, so an avatar and a "somebody" under it would be inventing a person —
                  the game's own name stands where the author's would. */}
              {isAlbum(photo) ? (
                <div className="cf-card__who">
                  <span className="cf-card__name">{photo.album.name}</span>
                  <span className="cf-card__when">
                    {shortDate(photo.created_at, i18n.language)}
                  </span>
                </div>
              ) : isGame(photo) ? (
                <div className="cf-card__who">
                  {/* The game's name, not its short code: "RYB" is what the team sheets are
                      printed with and means nothing to somebody looking at photographs. */}
                  <span className="cf-card__name">{photo.game.name}</span>
                  <span className="cf-card__when">
                    {shortDate(photo.created_at, i18n.language)}
                  </span>
                </div>
              ) : (
                <>
                  <Avatar url={photo.author_photo} name={photo.author} />
                  <div className="cf-card__who">
                    <span className="cf-card__name">{photo.author || t('feedSomeone')}</span>
                    <span className="cf-card__when">
                      {shortDate(photo.created_at, i18n.language)}
                    </span>
                  </div>
                </>
              )}
              {photo.event && (
                <button type="button" className="pw-lb__where"
                        onClick={() => navigate(`/public-event/${photo.event.id}`)}>
                  <Icon name="calendar" className="me-1" />{photo.event.name}
                </button>
              )}
            </div>

            {/* A picture attached to a post illustrates a sentence somebody wrote. Showing the
                picture without the sentence would be quoting half of it. */}
            {photo.post && photo.post.body && (
              <p className="cf-card__text">{photo.post.body}</p>
            )}

            {/* And a mission answer is an answer to a QUESTION: the photograph on its own is
                somebody's desk, the mission is why it is worth looking at. Its caption is the
                line the person wrote under it, exactly as the stream draws it. */}
            {photo.mission && (
              <>
                {photo.mission.mission_name && (
                  <p className="cf-card__text"><strong>{photo.mission.mission_name}</strong></p>
                )}
                {photo.mission.caption && (
                  <p className="cf-card__text">{photo.mission.caption}</p>
                )}
              </>
            )}

            {/* ★ The page's notice sits in the card at the TOP, which this overlay covers — so a
                refusal read as a button that does nothing at all. It is repeated where the press
                happened, which is the only place the person is looking. */}
            {notice && <p className="bravos-cal__notice">{notice}</p>}

            {/* And a game is a set of RULES: a photograph of eight people holding a rope is a
                photograph, "two teams, one rope, three pulls" is what makes it a game. The
                version that applied at THIS event, which is the one the row carries. */}
            {isGame(photo) && photo.game.rules && (
              <p className="cf-card__text pw-lb__rules">{photo.game.rules}</p>
            )}

            {/* ★ Everything below is about a card — the hearts, the thread, who is in the
                picture, the flag. A game has no card behind it, so none of it has anything to
                write to and all of it stands aside rather than being drawn dead. */}
            {!isGame(photo) && !isAlbum(photo) && (
            <>
            <div className="cf-card__acts">
              <button
                type="button"
                className={`cf-act ${bodyOf(photo).liked ? 'is-on' : ''}`}
                disabled={busy}
                onClick={() => like(photo)}
                aria-label={t('like')}
                title={t('like')}
              >
                <Icon name="heart" /> {bodyOf(photo).likes > 0 ? bodyOf(photo).likes : ''}
              </button>
              {/* Share leads where the browser has a sheet for it — on a phone that is what people
                  mean by sharing a photo, and it skips the downloads folder entirely. Spelled out
                  rather than left as a bare arrow, for the reason the report button below is. */}
              {shareable && (
                <button type="button" className="cf-act" disabled={saving}
                        onClick={() => savePicture(photo, sharePhoto)}
                        aria-label={t('share')} title={t('share')}>
                  <Icon name="share" /> {t('share')}
                </button>
              )}
              <button type="button" className="cf-act" disabled={saving}
                      onClick={() => savePicture(photo, downloadPhoto)}
                      aria-label={t('download')} title={t('download')}>
                <Icon name="download" /> {t('download')}
              </button>
              {(canModerate || photo.author_id === me) && (
                <button type="button" className="cf-act" disabled={busy}
                        onClick={() => remove(photo)}
                        aria-label={t('remove')} title={t('remove')}>
                  <Icon name="trash" />
                </button>
              )}
              {/* Straightening a picture is not moderating it — the photograph everybody uploaded
                  is the photograph everybody keeps, the right way up — so it is open to the same
                  two the album opens it to: the person who took it, and whoever runs the event.
                  Only an album photo has bytes of ours behind it; a post's picture and a mission
                  answer are somewhere else. Spelled out rather than drawn as a bare arrow, for
                  the reason the report button below is. */}
              {photo.photo && photo.event && (canModerate || photo.author_id === me) && (
                <>
                  <button type="button" className="cf-act" disabled={busy}
                          onClick={() => rotate(photo, -1)} title={t('rotateLeft')}>
                    <Icon name="rotate-left" /> {t('rotateLeft')}
                  </button>
                  <button type="button" className="cf-act" disabled={busy}
                          onClick={() => rotate(photo, 1)} title={t('rotateRight')}>
                    <Icon name="rotate-right" /> {t('rotateRight')}
                  </button>
                </>
              )}
              {/* Naming somebody. Only on an album photograph that belongs to an event: "who is in
                  this" is answered from that event's guest list, and a picture attached to a post
                  has no such list to answer from. A restricted person is not offered it either —
                  the choke point refuses a tag exactly as it refuses a comment. */}
              {photo.photo && photo.event && canPost && (
                <button type="button" className="cf-act" disabled={busy}
                        onClick={() => loadPeople(photo)}>
                  <Icon name="user-add" /> {t('tagSomeone')}
                </button>
              )}
              {/* ★ The flag says what it is. On its own it was a small shape people read as a
                  bookmark, a language switch or nothing at all — and a control nobody
                  recognises is a control nobody uses, which on a report button means somebody
                  looks at a picture they wanted gone and closes the app instead. The album
                  already spells its version out ("Ask to remove"), so this one does too. */}
              {photo.author_id !== me && (
                <button type="button" className="cf-act" disabled={busy}
                        onClick={() => setReporting(!reporting)}
                        aria-label={t('report')} title={t('report')}>
                  <Icon name="flag" /> {t('report')}
                </button>
              )}
            </div>

            {reporting && (
              <div className="cf-report">
                <p className="cf-card__when">{t('reportExplains')}</p>
                <select className="cf-input" value={reportReason}
                        onChange={(e) => setReportReason(e.target.value)}>
                  {REPORT_REASONS.map((r) => (
                    <option key={r} value={r}>{t(`reportReason_${r}`)}</option>
                  ))}
                </select>
                <textarea
                  className="cf-input"
                  rows={2}
                  maxLength={500}
                  value={reportNote}
                  placeholder={t('reportNotePlaceholder')}
                  onChange={(e) => setReportNote(e.target.value)}
                />
                <button type="button" className="bravos-submit" disabled={busy}
                        onClick={() => sendReport(photo)}>
                  {t('reportSend')}
                </button>
                <button type="button" className="cf-tool" onClick={() => setReporting(false)}>
                  {t('cancel')}
                </button>
              </div>
            )}

            {/* Who is in it. ★ The × is drawn only where it will work — your own name always,
                the one you wrote, and anybody's if you are the admin. The server asks the same
                question again; this is so nobody presses a cross that answers 403. */}
            {(bodyOf(photo).tags || []).length > 0 && (
              <div className="pw-lb__tags">
                {bodyOf(photo).tags.map((tag) => (
                  <span className="pw-tag" key={tag.partner_id}>
                    {tag.name}
                    {(tag.partner_id === me || tag.mine || canModerate) && (
                      <button type="button" className="pw-tag__x" disabled={busy}
                              aria-label={t('remove')} onClick={() => untag(photo, tag.partner_id)}>
                        ×
                      </button>
                    )}
                  </span>
                ))}
              </div>
            )}

            {tagging && (
              <div className="pw-lb__tagpick">
                <select
                  className="cf-input"
                  defaultValue=""
                  disabled={busy || people === null}
                  onChange={(e) => { if (e.target.value) tag(photo, e.target.value); }}
                >
                  <option value="">{people === null ? t('loading') : t('tagSomeone')}</option>
                  {(people || [])
                    .filter((p) => !(bodyOf(photo).tags || [])
                      .some((tg) => tg.partner_id === p.id))
                    .map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                {people !== null && people.length === 0 && (
                  <p className="cf-card__when">{t('tagNobodyToTag')}</p>
                )}
                <button type="button" className="cf-tool" onClick={() => setTagging(false)}>
                  {t('cancel')}
                </button>
              </div>
            )}

            {(bodyOf(photo).comments || []).map((c) => (
              <div key={c.id} className="cf-comment">
                <Avatar url={c.author_photo} name={c.author} />
                <div className="cf-comment__body">
                  <span className="cf-comment__who">{c.author}</span>
                  <span className="cf-comment__text">{c.body}</span>
                </div>
              </div>
            ))}

            {canPost && (
              <div className="cf-reply">
                <textarea
                  className="cf-input"
                  rows={2}
                  maxLength={MAX_BODY}
                  value={draft}
                  placeholder={t('feedCommentPlaceholder')}
                  onChange={(e) => setDraft(e.target.value)}
                />
                <button
                  type="button"
                  className="bravos-submit"
                  disabled={busy || !draft.trim()}
                  onClick={() => comment(photo)}
                >
                  {t('send')}
                </button>
              </div>
            )}
            {/* The server refuses the write anyway; this is so a restricted person reads why. */}
            {!canPost && (
              <p className="bravos-cal__notice">
                {t('postingRestricted')}
                {data.post_block_reason ? ` ${data.post_block_reason}` : ''}
              </p>
            )}
            </>
            )}
          </div>
        </div>
      )}
    </Base>
  );
};

export default PhotoWall;
